"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.collinear = collinear;
exports.collinearError = collinearError;
exports.distanceToLineSegment = distanceToLineSegment;
exports.pointLineProjection = pointLineProjection;
var _vector = require("./vector");
/**
 * 
 * @param px The x-coordinate of the test point
 * @param py The y-coordinate of the test point
 * @param ax The x-coordinate of the first point of the line segment
 * @param ay The y-coordinate of the first point of the line segment
 * @param bx The x-coordinate of the second point of the line segment
 * @param by The y-coordinate of the second point of the line segment
 * @param includeEndPoints If the point projected onto the line is not contained within the line segment, returns the distance to an endpoint. Otherwise, returns null.
 * @returns The perpendicular distance to the line segment, or null if the point is not within the line segment (unless includeEndPoints is true, then the distance to the nearest end point)
 */

function distanceToLineSegment(px, py, ax, ay, bx, by, includeEndPoints = false) {
  let p = {
    x: px,
    y: py
  };
  let a = {
    x: ax,
    y: ay
  };
  let b = {
    x: bx,
    y: by
  };
  let plp = pointLineProjection(p, a, b);
  if (!plp) {
    if (includeEndPoints) {
      return {
        distance: (0, _vector.norm)((0, _vector.sub)(p, a)),
        closestPoint: a,
        t: 0
      };
    } else {
      return null;
    }
  }
  let {
    t,
    d
  } = plp;

  // If the point is "inside" the line segment, then 0 <= t <= 1.
  if (t >= 0 && t <= 1) {
    // The point is within the line segment.
    const closestPoint = (0, _vector.add)((0, _vector.mul)(a, 1 - t), (0, _vector.mul)(b, t));
    const distance = Math.abs(d * (0, _vector.norm)((0, _vector.sub)(b, a)));
    return {
      distance,
      closestPoint,
      t
    };
  } else {
    if (includeEndPoints) {
      let distance, closestPoint;
      if (t > 0.5) {
        closestPoint = b;
      } else {
        closestPoint = a;
      }
      distance = (0, _vector.norm)((0, _vector.sub)(p, closestPoint));
      return {
        distance,
        closestPoint,
        t: Math.min(Math.max(t, 0), 1)
      };
    } else {
      return null;
    }
  }
}

/**
 * Transforms a given point to a new coordinate system by specifying two basis points. By checking the coordinates of the returned point, you can calculate the distance from the point to a line segment defined by the basis points, determine if the point projected onto that line falls within the segment, and so on.
 * @param p The point to transform
 * @param a The first basis point
 * @param b The second basis point
 * @returns A new point representing the test point in "line segment" coordinates. If the basis points are too close together, returns null.
 */
function pointLineProjection(p, a, b) {
  const ax = a.x,
    ay = a.y;
  const px = p.x - ax,
    py = p.y - ay;
  const bx = b.x - ax,
    by = b.y - ay;
  const den = bx * bx + by * by;
  if (den < 1e-28) {
    // The basis points are too close together.
    return null;
  }
  const t = (px * bx + py * by) / den;
  const d = (bx * py - by * px) / den;
  return {
    t,
    d
  };
}
/**
 * Tests whether two lines are collinear within a given tolerance.
 * @param {Line} e0 
 * @param {Line} e1 
 */
function collinear(e0, e1, collinearityTolerance) {
  let error = collinearError(e0, e1);
  return error < collinearityTolerance;
}

/**
 * Returns the maximum error between two supposedly collinear line segments, as measured at their furthest ends.
 * @param e0 The first line segment
 * @param e1 The second line segment
 * @returns The maximum error between the two line segments
 */
function collinearError(e0, e1) {
  let a = e0.start;
  let b = e0.end;
  let c = e1.start;
  let d = e1.end;

  // Project endpoints onto each other's line segments
  let aPlp = pointLineProjection(a, c, d);
  let bPlp = pointLineProjection(b, c, d);
  let cPlp = pointLineProjection(c, a, b);
  let dPlp = pointLineProjection(d, a, b);
  let abLen = (0, _vector.norm)((0, _vector.sub)(a, b));
  let cdLen = (0, _vector.norm)((0, _vector.sub)(c, d));

  // Find the maximum error
  let aError = aPlp ? Math.abs(aPlp.d * cdLen) : 0;
  let bError = bPlp ? Math.abs(bPlp.d * cdLen) : 0;
  let cError = cPlp ? Math.abs(cPlp.d * abLen) : 0;
  let dError = dPlp ? Math.abs(dPlp.d * abLen) : 0;
  return Math.max(aError, bError, cError, dError);
}